<template>
  <!-- Show Permission Denied when Point System is NONACTIVE -->
  <PermissionDenied v-if="!isPointSystemActive" />

  <!-- Show normal content when Point System is ACTIVE -->
  <div v-else>
  <!-- Desktop View -->
  <div class="desktop-view" v-if="!isMobile">
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">My Vouchers</h1>
        <p class="page-subtitle">Manage and view your voucher collection</p>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- Main Content -->
      <div class="main-content">
        <!-- Skeleton Loading State (only on first load) -->
        <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="voucher" :count="4" :is-grid="true" />

        <!-- Voucher List -->
        <div v-if="page.output && !(page.isLoading && page.isFirstLoad)" class="voucher-grid">
          <div v-for="(item, index) in displayedVoucher" :key="index" class="voucher-card">
            <div v-if="item.isNew" class="voucher-badge">NEW</div>
            <div class="voucher-content" @click="openVoucherModal(item)">
              <div class="voucher-img">
                <img :src="item.voucher?.avatar?.image_url || '/img/member-img/offer-example.svg'" alt="Voucher Image">
              </div>
              <div class="voucher-detail">
                <h1 class="voucher-title">
                  {{ item.voucher?.name || item.name }}
                </h1>
                <p class="voucher-description">
                  {{ getVoucherSubtext(item) }}
                </p>
                <div class="voucher-date">
                  Valid until • {{ formatDate(item.expired_at || item.expiration) }}
                </div>
                <div v-if="checkIsClaimed(item)" class="claimed-badge">
                  <i class="fas fa-check-circle" style="margin-right: 4px;"></i>Claimed
                </div>
                <div v-else-if="checkIsExpired(item)" class="expired-badge">
                  <i class="fas fa-times-circle" style="margin-right: 4px;"></i>Expired
                </div>
                <div v-else-if="checkIsExpiringSoon(item)" class="expiring-warning-badge">
                  <i class="fas fa-exclamation-circle" style="margin-right: 4px;"></i>Expiring in {{ getTimeRemaining(item) }}
                </div>
              </div>
            </div>
            <div class="voucher-perf"></div>
            <button class="detail-voucher-button">
              <img src="/img/member-icon/right.svg" alt="">
            </button>
          </div>
          <no-items v-if="displayedVoucher.length === 0" message="No vouchers available" transparent />
        </div>

        <!-- Pagination -->
        <member-pagination 
          v-if="!page.isLoading && displayedVoucher.length" 
          :page="meta.page" 
          :total="meta.total" 
          @callback="navPage"
        />
      </div>

      <!-- Sidebar Filter & Sort -->
      <div class="sidebar">
        <!-- Filter Section -->
        <div class="filter-card">
          <h3 class="card-title">Filter</h3>
          
          <div class="filter-group">
            <label class="filter-label">Unit / Branch</label>
            <div class="custom-select">
              <select v-model="filterOptions.branch">
                <option value="">All Branch</option>
                <option v-for="branch in uniqueBranches" :key="branch.id" :value="branch.id">
                  {{ branch.name }}
                </option>
              </select>
              <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">Merchant</label>
            <div class="custom-select">
              <select v-model="filterOptions.merchant" :disabled="!filterOptions.branch">
                <option value="">All Merchant</option>
                <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.id">
                  {{ merchant.name }}
                </option>
              </select>
              <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <div class="checkbox-group">
            <div class="checkbox-item">
              <input type="checkbox" v-model="filterOptions.hideExpired" id="hideExpired">
              <label for="hideExpired">Hide expired vouchers</label>
            </div>
            <div class="checkbox-item">
              <input type="checkbox" v-model="filterOptions.hideClaimed" id="hideClaimed">
              <label for="hideClaimed">Hide claimed vouchers</label>
            </div>
          </div>

          <div class="button-group">
            <button @click="applyFilter" class="btn-apply">Apply Filter</button>
            <button @click="clearFilter" class="btn-clear">Clear Filter</button>
          </div>
        </div>

        <!-- Sort Section -->
        <div class="filter-card">
          <h3 class="card-title">Sort</h3>
          
          <div class="filter-group">
            <label class="filter-label">Sort By</label>
            <div class="custom-select">
              <select v-model="sortOptions.sortBy">
                <option value="redemptionDate">Redemption Date</option>
              </select>
              <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">Sort Direction</label>
            <div class="custom-select">
              <select v-model="sortOptions.sortDirection">
                <option value="desc">Newest to Oldest</option>
                <option value="asc">Oldest to Newest</option>
              </select>
              <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <div class="button-group">
            <button @click="applySort" class="btn-apply">Apply Sort</button>
            <button @click="clearSort" class="btn-clear">Clear Sort</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile View -->
  <div class="mobile-view" v-else>
    <div class="top-background">
      <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="111" cy="-84" rx="219" ry="139" fill="currentColor"/>
      </svg>
    </div>
    
    <div class="mobile-header">
      <h2 class="mobile-title">My Vouchers</h2>
      <div class="mobile-actions">
        <button @click="openModal('filter')" class="filter-button">
          <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
        </button>
        <button @click="openModal('sort')" class="filter-button">
          <img src="/img/member-icon/sort-icon.svg" alt="sort-icon" width="14" height="14">
        </button>
      </div>
    </div>

    <!-- Skeleton Loading State (Mobile - only on first load) -->
    <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="voucher" :count="4" />

    <div class="voucher-list" v-if="!(page.isLoading && page.isFirstLoad)">
      <div v-for="(item, index) in displayedVoucher" :key="index" class="voucher-container">
        <div v-if="item.isNew" class="voucher-badge">NEW</div>
        <div class="voucher-content" @click="openVoucherModal(item)">
          <div class="voucher-img">
            <img :src="item.voucher?.avatar?.image_url || '/img/member-img/offer-example.svg'" alt="Voucher Image">
          </div>
          <div class="voucher-detail">
            <h1 class="voucher-title">
              {{ item.voucher?.name || item.name }}
            </h1>
            <p class="voucher-description">
              {{ getVoucherSubtext(item) }}
            </p>
            <div class="voucher-date">
              Valid until • {{ formatDate(item.expired_at || item.expiration) }}
            </div>
            <div v-if="checkIsClaimed(item)" class="claimed-badge">
              <i class="fas fa-check-circle" style="margin-right: 4px;"></i>Claimed
            </div>
            <div v-else-if="checkIsExpired(item)" class="expired-badge">
              <i class="fas fa-times-circle" style="margin-right: 4px;"></i>Expired
            </div>
            <div v-else-if="checkIsExpiringSoon(item)" class="expiring-warning-badge">
              <i class="fas fa-exclamation-circle" style="margin-right: 4px;"></i>Expiring in {{ getTimeRemaining(item) }}
            </div>
          </div>
        </div>
        <div class="voucher-perf"></div>
        <button class="detail-voucher-button">
          <img src="/img/member-icon/right.svg" alt="">
        </button>
      </div>
      <no-items v-if="displayedVoucher.length === 0 && page.output" message="No vouchers available" />
    </div>

    <!-- Pagination (Mobile) -->
    <member-pagination 
      v-if="!page.isLoading && displayedVoucher.length" 
      :page="meta.page" 
      :total="meta.total" 
      @callback="navPage"
    />

      <!-- Filter Modal -->
      <div v-if="isFilterModalOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Filter Vouchers</h3>
            <button @click="closeModal" class="close-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="filter-section">
              <div class="input-group">
                <label class="filter-label">Unit / Branch</label>
                <div class="custom-select">
                  <select v-model="filterOptions.branch">
                    <option value="">All Branch</option>
                    <option v-for="branch in uniqueBranches" :key="branch.id" :value="branch.id">
                      {{ branch.name }}
                    </option>
                  </select>
                  <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>

              <div class="input-group">
                <label class="filter-label">Merchant</label>
                <div class="custom-select">
                  <select v-model="filterOptions.merchant" :disabled="!filterOptions.branch">
                    <option value="">All Merchant</option>
                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.id">
                      {{ merchant.name }}
                    </option>
                  </select>
                  <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>

              <div class="checkbox-group">
                <div class="checkbox-item">
                  <input type="checkbox" v-model="filterOptions.hideExpired" id="hideExpired">
                  <label for="hideExpired">Hide expired vouchers</label>
                </div>
                <div class="checkbox-item">
                  <input type="checkbox" v-model="filterOptions.hideClaimed" id="hideClaimed">
                  <label for="hideClaimed">Hide claimed vouchers</label>
                </div>
              </div>

              <div class="button-group">
                <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                <button @click="clearFilter" class="btn-clear">Clear Filter</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sort Modal -->
      <div v-if="isSortModalOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Sort Vouchers</h3>
            <button @click="closeModal" class="close-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="sort-section">
              <div class="input-group">
                <label class="filter-label">Sort By</label>
                <div class="custom-select">
                  <select v-model="sortOptions.sortBy">
                    <option value="redemptionDate">Redemption Date</option>
                  </select>
                  <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>

              <div class="input-group">
                <label class="filter-label">Sort Direction</label>
                <div class="custom-select">
                  <select v-model="sortOptions.sortDirection">
                    <option value="desc">Newest to Oldest</option>
                    <option value="asc">Oldest to Newest</option>
                  </select>
                  <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>

              <div class="button-group">
                <button @click="applySort" class="btn-apply">Apply Sort</button>
                <button @click="clearSort" class="btn-clear">Clear Sort</button>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>

  <!-- Voucher Detail Modal -->
    <div v-if="isVoucherModalOpen" class="voucher-modal-backdrop" @click.self="closeVoucherModal">
      <div class="voucher-modal-card" ref="modalCardRef" :class="{ 'has-footer': isVoucherClaimed || selectedVoucherCancelable || isVoucherExpired }">
        <div class="voucher-hero" :style="voucherHeroStyle">
          <button class="voucher-modal-close" type="button" @click="closeVoucherModal">&times;</button>
          <span v-if="selectedVoucherPointDisplay" class="voucher-point-chip">{{ selectedVoucherPointDisplay }}</span>
        </div>

        <div class="voucher-modal-content">
          <div class="voucher-header">
            <h2 class="voucher-title">{{ selectedVoucherTitle }}</h2>
            <p class="voucher-merchant">{{ selectedVoucherMerchantInfo }}</p>
          </div>

          <div class="voucher-divider dashed"></div>

          <div class="voucher-meta-row">
            <div v-if="selectedVoucherValidUntil" class="meta-col">
              <i class="far fa-calendar-alt meta-icon"></i>
              <div class="meta-text">
                <span class="meta-label">Valid until</span>
                <span class="meta-value">{{ selectedVoucherValidUntil }}</span>
              </div>
            </div>

            <div v-if="selectedVoucherTimeRemaining" class="meta-col">
              <i class="fas fa-clock meta-icon"></i>
              <div class="meta-text">
                <span class="meta-label">Claim Before</span>
                <span class="meta-value">{{ selectedVoucherTimeRemaining }}</span>
              </div>
            </div>

            <div v-if="selectedVoucherCancelable && !isPastCancellationCutoff" class="meta-col">
              <i class="fas fa-undo-alt meta-icon"></i>
              <div class="meta-text">
                <span class="meta-label">Cancelable Until</span>
                <span class="meta-value">{{ cancellationCutoffDate }}</span>
              </div>
            </div>
          </div>

          <div class="voucher-desc-section">
            <div class="desc-header" @click="toggleDescription">
                <span class="desc-title">Description</span>
                <button class="desc-toggle-btn" type="button">
                    <i class="fas" :class="isDescriptionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
            </div>
            
            <div class="desc-content-wrapper" :class="{ 'is-expanded': isDescriptionExpanded }">
                <div class="voucher-description-html" v-html="selectedVoucherDescription" v-if="isDescriptionExpanded"></div>
                <div class="voucher-description-snippet" v-else>{{ truncatedDescription }}</div>
            </div>
          </div>

          <!-- Claimable Locations Section -->
          <div class="voucher-eligible-section" v-if="selectedVoucherEligibleMerchants.length > 0">
            <div class="eligible-header">
              <span class="eligible-title">
                <i class="fas fa-store eligible-title-icon"></i> Claimable Locations
              </span>
              <span class="eligible-count-badge">
                {{ isSelectedVoucherAllCorporate ? 'All Merchants' : `${selectedVoucherEligibleMerchants.length} Locations` }}
              </span>
            </div>
            
            <div class="eligible-list-container">
              <div 
                v-for="(item, idx) in selectedVoucherEligibleMerchants" 
                :key="idx" 
                class="eligible-merchant-item"
                :class="{ 'is-last': idx === selectedVoucherEligibleMerchants.length - 1 }"
              >
                <div class="merchant-info-group">
                  <i class="fas fa-map-marker-alt location-marker-icon"></i>
                  <div>
                    <div class="merchant-name">{{ item.merchant_name }}</div>
                    <div class="branch-name">{{ item.branch_name }}</div>
                  </div>
                </div>
                <span class="valid-status-badge">
                  Valid
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Smooth White Gradient & Text-Only Scroll Hint -->
        <div 
          v-if="showScrollHint" 
          class="scroll-hint-overlay"
        >
          <div class="scroll-hint-text" @click="scrollToBottom" title="Scroll down for details">
            <span>Scroll to see details</span>
            <i class="fas fa-chevron-down scroll-hint-icon"></i>
          </div>
        </div>

        <div class="voucher-footer" v-if="isVoucherClaimed || selectedVoucherCancelable || isVoucherExpired">
          <!-- Claimed Voucher Section -->
          <div v-if="isVoucherClaimed" class="voucher-cancel-info" style="background: #dcfce7; border-color: #bbf7d0;">
            <i class="fas fa-check-circle info-icon" style="color: #166534;"></i>
            <p class="cancel-info-text" style="color: #14532d;">
              Voucher has been claimed on <strong>{{ selectedVoucherClaimDate }}</strong>
            </p>
          </div>

          <!-- Cancel Voucher Section -->
          <template v-else-if="selectedVoucherCancelable && !isVoucherExpired">
            <div v-if="!isCanceling && !isPastCancellationCutoff" class="voucher-cancel-section">
              <button
                type="button"
                class="voucher-cancel-button"
                @click="startCanceling"
              >
                Cancel this voucher
              </button>
            </div>

            <div v-else-if="isCanceling && !isPastCancellationCutoff" class="voucher-cancel-form">
              <div class="cancel-reason-container">
                <label for="cancel-reason" class="cancel-reason-label">Reason for cancellation</label>
                <textarea 
                  id="cancel-reason" 
                  v-model="cancelReason" 
                  class="cancel-reason-input" 
                  maxlength="255"
                  placeholder="Please provide a reason for cancellation"
                ></textarea>
                <div class="cancel-reason-counter">{{ cancelReason.length }}/255</div>
              </div>
              <div class="cancel-button-container">
                <button class="confirm-cancel-button" @click="confirmCancellation">Confirm</button>
                <button class="abort-cancel-button" @click="abortCanceling">Abort</button>
              </div>
            </div>

            <div v-if="isPastCancellationCutoff" class="voucher-cancel-info voucher-cancel-warning">
              <i class="fas fa-exclamation-circle info-icon"></i>
              <p class="cancel-info-text">
                This voucher is <strong>{{ cancellationCutoffDate }}</strong> as the cancellation period has ended.
              </p>
            </div>
          </template>

          <div v-else-if="isVoucherExpired" class="voucher-cancel-info voucher-cancel-danger">
            <i class="fas fa-times-circle info-icon"></i>
            <p class="cancel-info-text">
              This voucher has <strong>{{ selectedVoucherTimeRemaining }}</strong> and can no longer be claimed.
            </p>
          </div>
        </div>
      </div>
    </div>
  <!-- Cancel Status Modal -->
  <VoucherCancelStatusModal
    v-if="isCancelStatusModalOpen"
    :status="cancelStatus"
    @close="closeCancelStatusModal"
  />
  </div>
</template>

<script>
import { computed, nextTick, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useModalHistory } from '@/composables/useModalHistory'
import VoucherCancelStatusModal from '@/components/member/VoucherCancelStatusModal'
import SkeletonLoader from '@/components/member/SkeletonLoader.vue'
import NoItems from '@/components/member/NoItems.vue'
import MemberPagination from '@/components/member/MemberPagination.vue'
import PermissionDenied from '@/views/PermissionDenied.vue'
import { usePointSystem } from '@/composables/pointsystem'

export default {
components: {
  VoucherCancelStatusModal,
  SkeletonLoader,
  NoItems,
  MemberPagination,
  PermissionDenied
},
setup() {
  const { isPointSystemActive } = usePointSystem()
  const store = useStore()
  const filteredVoucherState = ref([])
  const isFilterModalOpen = ref(false)
  const isSortModalOpen = ref(false)
  const isCancelStatusModalOpen = ref(false)
  const cancelStatus = ref('loading')
  
  // Setup modal history management untuk filter modal
  const { pushModalHistory: pushFilterModal, popModalHistory: popFilterModal } = useModalHistory({
    isVisible: isFilterModalOpen,
    onClose: () => {
      isFilterModalOpen.value = false
    },
    stateKey: 'voucher-filter-modal'
  })
  
  // Setup modal history management untuk sort modal
  const { pushModalHistory: pushSortModal, popModalHistory: popSortModal } = useModalHistory({
    isVisible: isSortModalOpen,
    onClose: () => {
      isSortModalOpen.value = false
    },
    stateKey: 'voucher-sort-modal'
  })

  const filterOptions = reactive({
    branch: '',
    merchant: '',
    hideExpired: true,
    hideClaimed: true
  })

  // Watcher: jika branch kosong, merchant juga dikosongkan
  watch(
    () => filterOptions.branch,
    (newVal) => {
      if (newVal === '') {
        filterOptions.merchant = ''
      }
    }
  )

  const sortOptions = reactive({
    sortBy: 'redemptionDate',
    sortDirection: 'desc'
  })

  const branchMerchantList = ref([])
  const fullResponseData = ref([])

  const meta = computed(() => store.state.ClientVoucher.meta);
  const filteredMerchants = computed(() => {
      if (filterOptions.branch) {
          const selectedBranch = branchMerchantList.value.find(branch => branch.id === filterOptions.branch);
          return selectedBranch ? selectedBranch.merchants : [];
      }

      let allMerchants = [];
      branchMerchantList.value.forEach(branch => {
          allMerchants = allMerchants.concat(branch.merchants);
      });
      return allMerchants;
  });

  const uniqueBranches = computed(() => {
    return branchMerchantList.value.map(branch => ({
      id: branch.id,
      name: branch.name
    }));
  });

  const isMobile = ref(window.innerWidth < 992)
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth < 992
  })

  const openModal = (type) => {
    if (type === 'filter') {
      isFilterModalOpen.value = true
      isSortModalOpen.value = false
      pushFilterModal()
    } else if (type === 'sort') {
      isSortModalOpen.value = true
      isFilterModalOpen.value = false
      pushSortModal()
    }
  }

  const closeModal = () => {
    if (isFilterModalOpen.value) {
      isFilterModalOpen.value = false
      popFilterModal()
    }
    if (isSortModalOpen.value) {
      isSortModalOpen.value = false
      popSortModal()
    }
  }

  const applySort = () => {
    applyFilter();
    closeModal();
  };

  const applyFilter = () => {
    const _meta = { ...meta.value, page: 1 };
    store.commit('ClientVoucher/switch_page', { meta: _meta });
    loadState();
  };

  const clearFilter = () => {
    filterOptions.branch = ''
    filterOptions.merchant = ''
    filterOptions.hideExpired = true
    filterOptions.hideClaimed = true
    applyFilter();
    closeModal()
  }

  const clearSort = () => {
    sortOptions.sortBy = 'redemptionDate'
    sortOptions.sortDirection = 'desc'
    applySort();
    closeModal()
  }

  const page = reactive({
    isLoading: true,
    output: false,
    isFirstLoad: true
  })

  const activeCardIndex = ref(null)
  const modalPosition = reactive({ top: 0, left: 0 })

  onMounted(() => {
    const _meta = { ...meta.value, page: 1 }
    store.commit('ClientVoucher/switch_page', { meta: _meta })

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('token')) {
        urlParams.delete('token');
        window.history.replaceState({}, document.title, window.location.pathname);
    }
    loadState()
  })

  const loadState = (options = {}) => {
    page.isLoading = true;

    const filter = {};

  if (filterOptions.branch) {
    filter.branch_id = filterOptions.branch;
  }

  if (filterOptions.merchant) {
    filter.merchant_id = filterOptions.merchant;
  }

  if (filterOptions.hideExpired) {
    filter.hide_expired = true;
  }

  if (filterOptions.hideClaimed) {
    filter.hide_claimed = true;
  }

  if (sortOptions.sortBy && sortOptions.sortDirection) {
    filter.sortBy = sortOptions.sortBy;
    filter.sortDirection = sortOptions.sortDirection;
  }

  store.commit('ClientVoucher/add_filter', { filter });

    store.dispatch('ClientVoucher/read')
      .then(response => {
        fullResponseData.value = response.data.data.vouchers;
        filteredVoucherState.value = [...fullResponseData.value];
        const branchesMap = new Map();

        response.data.data.branches.forEach(branchItem => {
          branchesMap.set(branchItem.id, {
            ...branchItem,
            merchants: branchItem.merchant || []
          });
        });

        branchMerchantList.value = Array.from(branchesMap.values());
        
        if (isMobile.value && (filterOptions.branch || filterOptions.merchant || sortOptions.sortBy)) {
            closeModal();
        }

        if (options.afterCancel && filteredVoucherState.value.length === 0 && meta.value.page > 1) {
          const previousPage = meta.value.page - 1;
          const _meta = { ...meta.value, page: previousPage };
          store.commit('ClientVoucher/switch_page', { meta: _meta });
          loadState();
        }

      })
      .catch(() => {
        // Error loading state
      })
      .finally(() => {
        page.isLoading = false;
        page.output = true;
        page.isFirstLoad = false;
      });
  };

  const navPage = (page) => {
    const _meta = { ...meta.value, page }
    store.commit('ClientVoucher/switch_page', { meta: _meta })
    loadState()
  }

  const toggleShading = (index, event) => {
    if (activeCardIndex.value === index) {
      activeCardIndex.value = null
    } else {
      activeCardIndex.value = index
      const rect = event.currentTarget.getBoundingClientRect()
      modalPosition.top = rect.top - 200 + window.scrollY
      modalPosition.left = rect.left
    }
  }

  const voucher = computed(() => store.state.ClientVoucher.voucher)
  
  const displayedVoucher = computed(() => {
    return filteredVoucherState.value;
  });

  // Voucher Modal State
  const isVoucherModalOpen = ref(false);
  const selectedVoucher = ref(null);
  const isCanceling = ref(false);
  const cancelReason = ref('');
  const isDescriptionExpanded = ref(false);
  
  // Setup modal history management untuk voucher detail modal
  const { pushModalHistory: pushVoucherModal, popModalHistory: popVoucherModal } = useModalHistory({
    isVisible: isVoucherModalOpen,
    onClose: () => {
      isVoucherModalOpen.value = false
      selectedVoucher.value = null
      isCanceling.value = false
      cancelReason.value = ''
      isDescriptionExpanded.value = false
    },
    stateKey: 'voucher-detail-modal'
  })

  const formatDate = (dateString, isUTC = false) => {
    if (!dateString) return 'No expiration';
    let dateStr = dateString;
    if (typeof dateStr === 'string' && dateStr.indexOf('T') === -1 && dateStr.indexOf('Z') === -1 && dateStr.length === 19) {
      dateStr = dateStr.replace(' ', 'T');
      if (isUTC) dateStr += 'Z';
    }
    const date = new Date(dateStr);
    
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate().toString().padStart(2, '0');
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day} ${monthName} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  // Voucher Modal Computed Properties
  const isSelectedVoucherAllCorporate = computed(() => {
    const v = selectedVoucher.value?.voucher || selectedVoucher.value;
    return v?.merchant_scope === 'all_corporate';
  });

  const selectedVoucherEligibleMerchants = computed(() => {
    const v = selectedVoucher.value?.voucher || selectedVoucher.value;
    if (!v) return [];

    if (Array.isArray(v.eligible_merchants_info) && v.eligible_merchants_info.length > 0) {
      return v.eligible_merchants_info;
    }
    if (Array.isArray(v.allowed_merchants) && v.allowed_merchants.length > 0) {
      return v.allowed_merchants.map(m => {
        if (typeof m === 'object' && m !== null) {
          return {
            merchant_name: m.name || m.text || m.merchant_name || 'Merchant',
            branch_name: m.branch?.name || m.branch_name || 'General Branch'
          };
        }
        return {
          merchant_name: m,
          branch_name: 'General Branch'
        };
      });
    }
    const merchantName = v.merchant?.name || selectedVoucher.value?.merchant?.name || selectedVoucher.value?.merchant_name;
    const branchName = v.merchant?.branch?.name || selectedVoucher.value?.branch?.name || selectedVoucher.value?.branch_name;
    if (merchantName) {
      return [{
        merchant_name: merchantName,
        branch_name: branchName || 'General Branch'
      }];
    }
    return [];
  });

  const selectedVoucherTitle = computed(() => {
    return selectedVoucher.value?.voucher?.name || selectedVoucher.value?.name || 'Membership Voucher';
  });

  const getVoucherSubtext = (item) => {
    const v = item?.voucher || item;
    if (v?.merchant_scope === 'all_corporate' || item?.merchant_scope === 'all_corporate') {
      return 'All Merchants & Branches';
    }
    if (Array.isArray(v?.eligible_merchants_info) && v.eligible_merchants_info.length > 1) {
      return `Claimable at ${v.eligible_merchants_info.length} Locations`;
    }
    if (Array.isArray(v?.allowed_merchants) && v.allowed_merchants.length > 1) {
      return `Claimable at ${v.allowed_merchants.length} Locations`;
    }
    const merchant = item.provider_name || item.corporate_name || item.merchant_name || item.merchant?.name || v.provider_name || v.corporate_name || v.merchant_name || v.merchant?.name;
    const branch = item.branch_name || item.branch?.name || v.branch_name || v.branch?.name;
    if (merchant && branch) {
      return `${branch} • ${merchant}`;
    }
    return merchant || branch || '—';
  };

  const selectedVoucherMerchantInfo = computed(() => {
    if (isSelectedVoucherAllCorporate.value) {
      return 'Claimable at All Merchants & Branches';
    }
    const count = selectedVoucherEligibleMerchants.value.length;
    if (count > 1) {
      return `Claimable at ${count} Locations`;
    }
    const merchant = selectedVoucher.value?.provider_name || selectedVoucher.value?.corporate_name || selectedVoucher.value?.merchant?.name || selectedVoucher.value?.merchant_name;
    const branch = selectedVoucher.value?.branch?.name || selectedVoucher.value?.branch_name;
    if (merchant && branch) {
      return `${merchant} • ${branch}`;
    }
    return merchant || branch || '—';
  });

  const selectedVoucherDescription = computed(() => {
    return selectedVoucher.value?.voucher?.desc
      || selectedVoucher.value?.desc
      || selectedVoucher.value?.description
      || 'Redeem this voucher to enjoy exclusive member privileges.';
  });

  const truncatedDescription = computed(() => {
    const desc = selectedVoucherDescription.value || '';
    const plainText = desc.replace(/<[^>]+>/g, '').trim();
    if (plainText.length <= 25) return plainText;
    return plainText.substring(0, 25) + '...';
  });

  const toggleDescription = () => {
    isDescriptionExpanded.value = !isDescriptionExpanded.value;
    nextTick(() => checkScroll());
  };

  const selectedVoucherPointDisplay = computed(() => {
    const points = selectedVoucher.value?.voucher?.point || selectedVoucher.value?.point;
    if (points === undefined || points === null) return null;
    return `${points} POINT` + (Number(points) === 1 ? '' : 'S');
  });

  const voucherHeroStyle = computed(() => {
    const image = selectedVoucher.value?.voucher?.avatar?.image_url
      || selectedVoucher.value?.avatar?.image_url
      || selectedVoucher.value?.image_url
      || '/img/member-img/offer-example.svg';
    return {
      backgroundImage: `url(${image})`
    };
  });

  const selectedVoucherValidUntil = computed(() => {
    const date = selectedVoucher.value?.expiration || selectedVoucher.value?.expired_at;
    if (!date) return null;
    return formatDate(date);
  });

  const selectedVoucherTimeRemaining = computed(() => {
    const dateStr = selectedVoucher.value?.expiration || selectedVoucher.value?.expired_at;
    if (!dateStr) return null;
    
    let parsedDateStr = dateStr;
    if (typeof parsedDateStr === 'string' && parsedDateStr.indexOf('T') === -1 && parsedDateStr.indexOf('Z') === -1 && parsedDateStr.length === 19) {
      parsedDateStr = parsedDateStr.replace(' ', 'T');
    }
    const expiryDate = new Date(parsedDateStr);
    const now = new Date();
    const diffMs = expiryDate - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} days ${diffHours} hours`;
    }
    
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffHours > 0) {
      return `${diffHours} hours ${diffMinutes} mins`;
    }
    
    return `${diffMinutes} mins`;
  });

  const isVoucherClaimed = computed(() => {
    return !!selectedVoucher.value?.claimed_at;
  });

  const selectedVoucherClaimDate = computed(() => {
    const dateStr = selectedVoucher.value?.claimed_at;
    if (!dateStr) return '';
    
    let parsedDateStr = dateStr;
    if (typeof parsedDateStr === 'string' && parsedDateStr.indexOf('T') === -1 && parsedDateStr.indexOf('Z') === -1 && parsedDateStr.length === 19) {
      parsedDateStr = parsedDateStr.replace(' ', 'T');
    }
    
    const date = new Date(parsedDateStr);
    
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate().toString().padStart(2, '0');
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day} ${monthName} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
  });

  const isVoucherExpired = computed(() => {
    const dateStr = selectedVoucher.value?.expiration || selectedVoucher.value?.expired_at;
    if (!dateStr) return false;
    
    const expiryDate = new Date(dateStr);
    const now = new Date();
    return (expiryDate - now) <= 0;
  });

  const checkIsClaimed = (item) => {
    return !!item.claimed_at;
  };

  const checkIsExpired = (item) => {
    const dateStr = item.expired_at || item.expiration;
    if (!dateStr) return false;
    
    const expiryDate = new Date(dateStr);
    const now = new Date();
    return (expiryDate.getTime() - now.getTime()) <= 0;
  };

  const checkIsExpiringSoon = (item) => {
    const dateStr = item.expired_at || item.expiration;
    if (!dateStr) return false;
    
    const expiryDate = new Date(dateStr);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    
    if (diffMs <= 0) return false; // Already expired
    
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  const getTimeRemaining = (item) => {
    const dateStr = item.expired_at || item.expiration;
    if (!dateStr) return '';
    
    const expiryDate = new Date(dateStr);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} days ${diffHours} hours`;
    }
    
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffHours > 0) {
      return `${diffHours} hours ${diffMinutes} mins`;
    }
    
    return `${diffMinutes} mins`;
  };

  const selectedVoucherCancelable = computed(() => {
    // Gift vouchers are never cancelable
    if (selectedVoucher.value?.source === 'gift' || selectedVoucher.value?.type === 'gift') {
      return false;
    }
    return selectedVoucher.value?.voucher?.cancelable === 1 || selectedVoucher.value?.cancelable === 1;
  });

  const cancellationCutoffDate = computed(() => {
    if (selectedVoucherCancelable.value) {
      let cutoffDate;
      if (selectedVoucher.value?.cancelable_before) {
        cutoffDate = new Date(selectedVoucher.value.cancelable_before);
      } else if (selectedVoucher.value?.voucher?.cancellation_cutoff && selectedVoucher.value?.expired_at) {
        const expirationTime = new Date(selectedVoucher.value.expired_at).getTime();
        cutoffDate = new Date(expirationTime - (selectedVoucher.value.voucher.cancellation_cutoff * 1000));
      } else if (selectedVoucher.value?.expired_at) {
        cutoffDate = new Date(selectedVoucher.value.expired_at);
      }

      if (cutoffDate) {
        const diffMs = cutoffDate - new Date();
        if (diffMs <= 0) return 'Non-refundable';
        
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (diffDays > 0) {
          return `${diffDays} days ${diffHours} hours`;
        }
        
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if (diffHours > 0) {
          return `${diffHours} hours ${diffMinutes} mins`;
        }
        
        return `${diffMinutes} mins`;
      }
    }
    return null;
  });

  const isPastCancellationCutoff = computed(() => {
    if (selectedVoucherCancelable.value) {
      let cutoffDate;
      if (selectedVoucher.value?.cancelable_before) {
        cutoffDate = new Date(selectedVoucher.value.cancelable_before);
      } else if (selectedVoucher.value?.voucher?.cancellation_cutoff && selectedVoucher.value?.expired_at) {
        const expirationTime = new Date(selectedVoucher.value.expired_at).getTime();
        cutoffDate = new Date(expirationTime - (selectedVoucher.value.voucher.cancellation_cutoff * 1000));
      } else if (selectedVoucher.value?.expired_at) {
        cutoffDate = new Date(selectedVoucher.value.expired_at);
      }

      if (cutoffDate) {
        const currentDate = new Date();
        return currentDate.getTime() >= cutoffDate.getTime();
      }
    }
    return true;
  });

  const modalCardRef = ref(null);
  const showScrollHint = ref(false);

  const checkScroll = () => {
    const el = modalCardRef.value;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight + 15;
    const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 35;
    showScrollHint.value = isScrollable && !isNearBottom;
  };

  const scrollToBottom = () => {
    const el = modalCardRef.value;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  };

  // Voucher Modal Methods
  const openVoucherModal = (voucher) => {
    selectedVoucher.value = voucher;
    isVoucherModalOpen.value = true;
    isCanceling.value = false;
    cancelReason.value = '';
    pushVoucherModal();
    nextTick(() => {
      checkScroll();
      if (modalCardRef.value) {
        modalCardRef.value.addEventListener('scroll', checkScroll);
      }
    });
  };

  const closeVoucherModal = () => {
    if (isCanceling.value) {
      // Jika sedang dalam mode cancel, tanyakan konfirmasi sebelum menutup
      if (confirm('Are you sure you want to close? Any cancellation reason will be lost.')) {
        if (modalCardRef.value) {
          modalCardRef.value.removeEventListener('scroll', checkScroll);
        }
        isCanceling.value = false;
        cancelReason.value = '';
        isVoucherModalOpen.value = false;
        selectedVoucher.value = null;
        showScrollHint.value = false;
        popVoucherModal();
      }
    } else {
      if (modalCardRef.value) {
        modalCardRef.value.removeEventListener('scroll', checkScroll);
      }
      isVoucherModalOpen.value = false;
      selectedVoucher.value = null;
      showScrollHint.value = false;
      popVoucherModal();
    }
  };

  const startCanceling = () => {
    isCanceling.value = true;
  };

  const abortCanceling = () => {
    isCanceling.value = false;
    cancelReason.value = '';
  };

  const confirmCancellation = async () => {
    if (cancelReason.value.trim() === '') {
      alert('Please provide a reason for cancellation.');
      return;
    }
    if (cancelReason.value.length > 255) {
      alert('Reason cannot exceed 255 characters.');
      return;
    }

    try {
      const data = {
        voucher_id: selectedVoucher.value.id,
        reason: cancelReason.value
      };

      await store.dispatch('ClientVoucher/cancellation', { data });
      
      // Refresh userInfo agar QR modal dan komponen lain mendapat data terbaru
      await store.dispatch('Auth/auth_check');
      
      // Tampilkan modal success
      isCancelStatusModalOpen.value = true;
      cancelStatus.value = 'success';
      isCanceling.value = false;
      closeVoucherModal();
      loadState({ afterCancel: true });
    } catch (error) {
      alert(`Cancellation failed: ${error.message}`);
    }
  };

  const closeCancelStatusModal = () => {
    isCancelStatusModalOpen.value = false;
    cancelStatus.value = 'loading';
  };

  onBeforeUnmount(() => {
    document.body.style.overflow = ''
  })

  watch([isFilterModalOpen, isSortModalOpen, isVoucherModalOpen], (vals) => {
    const anyOpen = vals.some(Boolean)
    document.body.style.overflow = anyOpen ? 'hidden' : ''
  })

  return {
    page,
    meta,
    loadState,
    voucher,
    navPage,
    toggleShading,
    activeCardIndex,
    isMobile,
    isFilterModalOpen,
    isSortModalOpen,
    filterOptions,
    sortOptions,
    branchMerchantList,
    uniqueBranches,
    filteredMerchants,
    openModal,
    closeModal,
    applyFilter,
    clearFilter,
    applySort,
    filteredVoucherState,
    displayedVoucher,
    clearSort,
    formatDate,
    checkIsClaimed,
    checkIsExpired,
    checkIsExpiringSoon,
    getTimeRemaining,
    // Voucher Modal Functions
    isVoucherClaimed,
    selectedVoucherClaimDate,
    isVoucherExpired,
    isVoucherModalOpen,
    selectedVoucher,
    openVoucherModal,
    closeVoucherModal,
    startCanceling,
    abortCanceling,
    confirmCancellation,
    selectedVoucherTitle,
    getVoucherSubtext,
    isSelectedVoucherAllCorporate,
    selectedVoucherEligibleMerchants,
    selectedVoucherMerchantInfo,
    selectedVoucherDescription,
    truncatedDescription,
    isDescriptionExpanded,
    toggleDescription,
    selectedVoucherPointDisplay,
    voucherHeroStyle,
    selectedVoucherValidUntil,
    selectedVoucherTimeRemaining,
    selectedVoucherCancelable,
    cancellationCutoffDate,
    isPastCancellationCutoff,
    isCanceling,
    cancelReason,
    modalCardRef,
    showScrollHint,
    scrollToBottom,
    isCancelStatusModalOpen,
    cancelStatus,
    closeCancelStatusModal,
    isPointSystemActive
  }
}
}
</script>

<style scoped>

  .detail-voucher-button {
    border: none;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;
  }
  .voucher-content {
    display: flex;
    gap: 36px;
    align-items: center;
    padding-left: 0;
    flex: 1;
    min-width: 0;
  }

  .voucher-detail {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 768px) {
    .voucher-content {
      gap: 36px;
      padding: 0;
    }
  }
  .voucher-img {
    width: 80px;
    min-width: 80px;
    height: 80px;
    min-height: 80px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }

  .voucher-img img {
    margin: auto;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .voucher-container {
    display: flex;
    flex-direction: row;
    padding: 18px 14px;
    background-color: #fff;
    gap: 12px;
    border-radius: 8px;
    align-items: center;
    position: relative;
    overflow: hidden;
    --cut-x: calc(14px + 88px + 12px);
  }

  .voucher-badge {
    position: absolute;
    top: -14px;
    left: -4px;
    width: 92px;
    height: 28px;
    background: var(--primary-color, #F93E3E);
    color: #fff;
    font-size: 8px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
    line-height: 34px;
    transform: translate(-30px, 12px) rotate(-45deg);
    border-radius: 4px;
    z-index: 2;
    pointer-events: none;
  }

  /* Voucher Modal Styles */
  .voucher-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 1000;
  }

  .voucher-modal-card {
    position: relative;
    width: min(480px, 100%);
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);
    max-height: 90vh;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .voucher-modal-card::-webkit-scrollbar {
    display: none; /* Hide scrollbar Chrome, Safari, Opera */
    width: 0;
    height: 0;
  }

  .scroll-hint-overlay {
    position: sticky;
    bottom: 16px;
    left: 0;
    right: 0;
    width: 100%;
    height: 55px;
    background: linear-gradient(to top, #ffffff 40%, rgba(255, 255, 255, 0.85) 75%, rgba(255, 255, 255, 0) 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 6px;
    pointer-events: none;
    z-index: 5;
    margin-top: -55px;
    margin-bottom: 0;
    transition: opacity 0.3s ease;
  }

  .voucher-modal-card.has-footer .scroll-hint-overlay {
    bottom: 92px;
  }

  .scroll-hint-text {
    pointer-events: auto;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    animation: bounceHintText 2s infinite;
    user-select: none;
    letter-spacing: 0.1px;
  }

  .scroll-hint-text:hover {
    color: var(--primary-color, #F93E3E);
  }

  .scroll-hint-icon {
    font-size: 10px;
    color: var(--primary-color, #F93E3E);
  }

  @keyframes bounceHintText {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(3px);
    }
    60% {
      transform: translateY(1.5px);
    }
  }

  .voucher-modal-close {
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 2;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid rgba(209, 213, 219, 0.8);
    background: rgba(255, 255, 255, 0.95);
    color: #0f172a;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .voucher-modal-close:hover {
    background: rgba(255, 255, 255, 1);
  }

  .voucher-hero {
    position: sticky;
    top: 0;
    z-index: 1;
    width: 100%;
    aspect-ratio: 16 / 9;
    background-color: #ffffff !important;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  .voucher-point-chip {
    position: absolute;
    top: 16px;
    left: 16px;
    background: rgba(255, 255, 255, 0.95);
    color: #0f172a;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    backdrop-filter: blur(8px);
  }

  .voucher-modal-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  }

  .voucher-header {
    margin-bottom: 20px;
  }

  .voucher-title {
    font-size: 20px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 8px 0;
    line-height: 1.3;
  }

  .voucher-merchant {
      margin: 0;
      font-size: 14px;
      color: #717784;
      line-height: 1.4;
  }

  .voucher-divider.dashed {
      height: 1px;
      width: 100%;
      border-top: none;
      background: repeating-linear-gradient(
          to right,
          #E2E8F0,
          #E2E8F0 6px,
          transparent 6px,
          transparent 12px
      );
      margin: 8px 0;
  }

  .voucher-description-html {
      font-size: 13px;
      color: #4B5563;
      line-height: 1.6;
  }

  .voucher-description-html :deep(p) {
      margin-bottom: 8px;
  }

  .voucher-description-html :deep(ul),
  .voucher-description-html :deep(ol) {
      margin-bottom: 8px;
      padding-left: 20px;
  }

  .voucher-description-html :deep(h1),
  .voucher-description-html :deep(h2),
  .voucher-description-html :deep(h3),
  .voucher-description-html :deep(h4),
  .voucher-description-html :deep(h5),
  .voucher-description-html :deep(h6) {
      margin-top: 12px;
      margin-bottom: 8px;
      font-weight: 600;
      color: #111827;
  }

  .voucher-description-html :deep(h1) { font-size: 18px; }
  .voucher-description-html :deep(h2) { font-size: 16px; }
  .voucher-description-html :deep(h3),
  .voucher-description-html :deep(h4),
  .voucher-description-html :deep(h5),
  .voucher-description-html :deep(h6) { font-size: 14px; }

  .voucher-description-html :deep(h1:first-child),
  .voucher-description-html :deep(h2:first-child),
  .voucher-description-html :deep(h3:first-child),
  .voucher-description-html :deep(p:first-child) {
      margin-top: 0;
  }

  .voucher-description-html :deep(p:last-child) {
      margin-bottom: 0;
  }

  .voucher-description-html :deep(a) {
      color: var(--primary-color, #D91A2A);
      text-decoration: none;
  }

  .voucher-description-html :deep(a:hover) {
      text-decoration: underline;
  }

  .voucher-description-html :deep(blockquote) {
      border-left: 3px solid #d1d5db;
      padding-left: 12px;
      margin-left: 0;
      margin-right: 0;
      color: #64748B;
      font-style: italic;
      margin-bottom: 8px;
      background: #F8FAFC;
      padding-top: 4px;
      padding-bottom: 4px;
      border-radius: 0 4px 4px 0;
  }

  .voucher-description-snippet {
      font-size: 14px;
      color: #475569;
      padding-bottom: 16px;
      line-height: 1.5;
  }

  .voucher-meta-row {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      border: 1px solid #F1F5F9;
      border-radius: 16px;
      padding: 16px;
      gap: 16px;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }

  @media (min-width: 500px) {
      .voucher-meta-row {
          flex-wrap: nowrap;
      }
  }

  .meta-col {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 130px;
  }

  @media (min-width: 500px) {
      .meta-col {
          min-width: 0;
      }
  }

  .meta-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: #F8FAFC;
      color: #64748B;
      font-size: 10px;
      flex-shrink: 0;
  }

  .meta-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
  }

  .meta-label {
      font-size: 11px;
      color: #64748B;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
  }

  .meta-value {
      font-size: 12px;
      color: #0f172a;
      font-weight: 700;
  }

  .voucher-desc-section {
      background: #ffffff;
      border: 1px solid #F1F5F9;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }

  .desc-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      margin-bottom: 16px;
  }

  .desc-title {
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
  }

  .desc-toggle-btn {
      background: none;
      border: none;
      color: #64748B;
      font-size: 14px;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.2s ease;
  }

  .desc-toggle-btn:hover {
      color: #0f172a;
  }

  .desc-content-wrapper {
      position: relative;
      transition: all 0.3s ease;
  }

  .voucher-footer {
      position: sticky;
      bottom: 0;
      z-index: 1;
      padding: 16px 24px 24px;
      background: #ffffff;
      border-top: 1px solid #F1F5F9;
  }

  .voucher-cancel-section {
    margin-top: 24px;
  }

  .voucher-cancel-button {
    width: 100%;
    padding: 12px 16px;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .voucher-cancel-button:hover {
    background: #dc2626;
  }

  .voucher-cancel-form {
    margin-top: 24px;
  }

  .cancel-reason-container {
    margin-bottom: 16px;
  }

  .cancel-reason-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }

  .cancel-reason-input {
    width: 100%;
    min-height: 80px;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    resize: vertical;
    margin-bottom: 8px;
  }

  .cancel-reason-input:focus {
    outline: none;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
  }

  .cancel-reason-counter {
    font-size: 12px;
    color: #6b7280;
    text-align: right;
  }

  .cancel-button-container {
    display: flex;
    gap: 12px;
  }

  .confirm-cancel-button {
    flex: 1;
    padding: 12px 16px;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .confirm-cancel-button:hover {
    background: #dc2626;
  }

  .abort-cancel-button {
    flex: 1;
    padding: 12px 16px;
    background: #6b7280;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .abort-cancel-button:hover {
    background: #4b5563;
  }

  .voucher-cancel-info {
    margin-top: 16px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .voucher-cancel-warning {
    background: #fffbeb;
    border-color: #fef3c7;
  }

  .voucher-cancel-warning .info-icon {
    color: #f59e0b;
  }

  .voucher-cancel-warning .cancel-info-text {
    color: #92400e;
  }

  .voucher-cancel-danger {
    background: #fef2f2;
    border-color: #fee2e2;
  }

  .voucher-cancel-danger .info-icon {
    color: #ef4444;
  }

  .voucher-cancel-danger .cancel-info-text {
    color: #991b1b;
  }

  .info-icon {
    font-size: 16px;
    margin-top: 2px;
  }

  .cancel-info-text {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.4;
  }

  .expiring-warning-badge {
    display: inline-flex;
    align-items: center;
    color: #b45309;
    background: #fef3c7;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    margin-top: 4px;
  }

  .claimed-badge {
    display: inline-flex;
    align-items: center;
    color: #166534;
    background: #dcfce7;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    margin-top: 4px;
  }

  .expired-badge {
    display: inline-flex;
    align-items: center;
    color: #991b1b;
    background: #fef2f2;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    margin-top: 4px;
  }

  .voucher-container::before,
  .voucher-container::after {
    content: "";
    position: absolute;
    left: var(--cut-x);
    width: 28px;
    height: 28px;
    background: #eef3f3;
    border-radius: 50%;
    transform: translateX(-50%);
    pointer-events: none;
  }

  .voucher-container::before {
    top: -19px;
    box-shadow: 0 -1px 0 #E1E4EA inset;
  }

  .voucher-container::after {
    bottom: -19px;
    box-shadow: 0 1px 0 #E1E4EA inset;
  }

  .voucher-detail {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .voucher-perf {
    position: absolute;
    left: var(--cut-x);
    top: 18px;
    bottom: 18px;
    border-left: 2px dashed #D1D5DB;
  }

  .voucher-title {
      font-size: 14px;
      font-weight: bold;
      color: #111827;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 4px;
  }

  .voucher-description {
      font-size: 12px;
      font-weight: normal;
      color: #111827;
      line-height: 1.4;
  }

  .voucher-date {
      font-size: 12px;
      font-weight: normal;
      color: #99A0AE;
      line-height: 1.4;
      white-space: nowrap;
  }
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  .top-background {
    position: absolute;
    top: -8px;
    left: -32px;
    color: var(--primary-color, #F93E3E);
  }

  .mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 64px;
    margin-bottom: 14px;
}

.mobile-title {
    font-size: 18px;
    font-weight: 520;
    margin: 0 !important;
}

  .mobile-actions {
    display: flex;
    gap: 8px;
  }

  .voucher-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

.icon-button {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-color, #F93E3E);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.filter-button {
    background-color: var(--primary-color, #F93E3E);
    border: none;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background-color 0.3s;
}

.filter-button img {
    width: 14px;
    height: 14px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.close-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6B7280;
}

.modal-body {
  padding: 20px;
}

.input-group {
  margin-bottom: 16px;
}

.filter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.custom-select {
  position: relative;
  width: 100%;
}

.custom-select select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: white;
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: all 0.2s;
}

.custom-select select:hover {
  border-color: #D1D5DB;
}

.custom-select select:focus {
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
}

.select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6B7280;
}

.checkbox-group {
  margin-bottom: 20px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #D1D5DB;
  cursor: pointer;
}

.checkbox-item input[type="checkbox"]:checked {
  accent-color: var(--primary-color, #F93E3E);
}

.checkbox-item label {
  font-size: 14px;
  color: #374151;
  margin: 0;
  cursor: pointer;
}

.button-group {
  display: flex;
  gap: 12px;
}

.btn-apply,
.btn-clear {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-apply {
  background-color: var(--primary-color, #F93E3E);
  color: white;
}

.btn-apply:hover {
  opacity: 0.9;
}

.btn-clear {
  background-color: white;
  color: #374151;
  border: 1px solid #E5E7EB;
}

.btn-clear:hover {
  background-color: #F9FAFB;
}

.filter-section,
.sort-section {
  display: flex;
  flex-direction: column;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

/* Desktop & Tablet Styles */
.desktop-view {
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px;
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  /* max-width: 1280px; */
  margin: 0 auto;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 24px;
  margin: 0 auto;
}

.main-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 140px);
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.filter-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
}

.filter-group {
  margin-bottom: 20px;
}

.checkbox-group {
  margin: 24px 0;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  cursor: pointer;
}

.checkbox-item label {
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.button-group {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

/* Loading removed - using skeleton instead */

.pagination-container {
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

/* Voucher Grid Layout for Desktop */
.voucher-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.voucher-card {
  display: flex;
  flex-direction: row;
  padding: 18px 14px;
  background-color: #fff;
  gap: 12px;
  border-radius: 8px;
  align-items: center;
  position: relative;
  overflow: hidden;
  --cut-x: calc(14px + 80px + 8px);
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s ease;
}

.voucher-card::before,
.voucher-card::after {
  content: "";
  position: absolute;
  left: var(--cut-x);
  width: 28px;
  height: 28px;
  background: #e8eaed;
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
}

.voucher-card::before {
  top: -19px;
  box-shadow: 0 -1px 0 #E1E4EA inset;
}

.voucher-card::after {
  bottom: -19px;
  box-shadow: 0 1px 0 #E1E4EA inset;
}

.voucher-card .voucher-perf {
  position: absolute;
  left: calc(var(--cut-x) + 13px); /* Center alignment with cut circles */
  top: 18px;
  bottom: 18px;
  border-left: 2px dashed #D1D5DB;
  z-index: 0;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-wrapper {
    grid-template-columns: 1fr 280px;
    gap: 16px;
  }
  
  .filter-card {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .voucher-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    padding-bottom: 96px;
  }
}

@media (min-width: 992px) {
  .mobile-view {
    display: none;
  }
  
  .voucher-list {
    display: none;
  }
}

/* Hover effects for desktop - Simplified with red shadow */
@media (hover: hover) {
  .voucher-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
  
  .detail-voucher-button:hover {
    background-color: #f3f4f6;
  }
}

/* Focus states for accessibility */
.voucher-card:focus-within {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.filter-button:focus,
.detail-voucher-button:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Empty state styling */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  grid-column: 1 / -1;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

.voucher-eligible-section {
  margin-top: 12px;
  text-align: left;
}

.eligible-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.eligible-title {
  font-size: 13px;
  font-weight: 600;
  color: #1E293B;
}

.eligible-title-icon {
  color: var(--primary-color, #D91A2A);
  margin-right: 4px;
}

.eligible-count-badge {
  font-size: 11px;
  font-weight: 600;
  color: var(--primary-color, #D91A2A);
  background-color: rgba(217, 26, 42, 0.08);
  border: 1px solid rgba(217, 26, 42, 0.15);
  padding: 2px 8px;
  border-radius: 999px;
}

.eligible-list-container {
  padding: 8px;
  border-radius: 8px;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  max-height: 160px;
  overflow-y: auto;
}

.eligible-merchant-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-bottom: 1px solid #F1F5F9;
}

.eligible-merchant-item.is-last {
  border-bottom: none;
}

.merchant-info-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.location-marker-icon {
  color: #EF4444;
  font-size: 12px;
}

.merchant-name {
  font-size: 12px;
  font-weight: 600;
  color: #0F172A;
}

.branch-name {
  font-size: 11px;
  color: #64748B;
}

.valid-status-badge {
  font-size: 10px;
  font-weight: 600;
  color: #15803D;
  background-color: #DCFCE7;
  border: 1px solid #BBF7D0;
  padding: 2px 6px;
  border-radius: 4px;
}

</style>